import React, { FunctionComponent, useCallback, useEffect, useState } from "react"
import { useDropzone } from "react-dropzone"
import { Paper, Typography, Grid, Link, Box } from "@material-ui/core"
import PublishIcon from "@material-ui/icons/Publish"
import styles from "./FileUploadField.module.scss"
interface FileUploadFieldProps {
    acceptTypes: string[]
    onUpload: (file: File) => void
}

export const FileUploadField: FunctionComponent<FileUploadFieldProps> = ({
    acceptTypes,
    onUpload
}) => {
    const [filename, setFilename] = useState("")
    const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
        accept: acceptTypes,
        maxFiles: 1
    })

    const acceptFile = useCallback(
        (file: File) => {
            setFilename(file.name)
            onUpload(file)
        },
        [onUpload]
    )

    useEffect(() => {
        // having `acceptFile` as a dependency forces multiple re-renders.
        if (acceptedFiles.length === 1) {
            acceptFile(acceptedFiles[0])
        }
    }, [acceptedFiles])

    // html input type:file doesn't recognize file as new if same filename as last upload attempt
    // clear event value on click so that new event is recognized
    const clearPreviousFile = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        const element = event.target as HTMLInputElement
        element.value = ""
    }

    return (
        <Paper
            className={styles.FileUploadField__dropzone}
            id="file-upload-field-dropzone"
            data-testid="file-upload-field-dropzone"
            {...getRootProps({ onClick: e => e.stopPropagation() })}
            variant="outlined"
        >
            <Grid
                className={styles.FileUploadField__container}
                container
                direction="column"
                alignItems="center"
                justify="center"
            >
                <Grid
                    className={styles.FileUploadField__content}
                    item
                    container
                    direction="column"
                    alignItems="center"
                    justify="center"
                >
                    <Grid
                        item
                        xs
                        container
                        alignItems="center"
                        justify="center"
                        className={styles.FileUploadField__iconNameContainer}
                    >
                        {filename && (
                            <Typography
                                className={styles.FileUploadField__filename}
                                variant="caption"
                            >
                                {filename}
                            </Typography>
                        )}

                        {!filename && <PublishIcon fontSize="large" color="primary" />}
                    </Grid>
                    <Grid item xs container alignItems="center" justify="center" spacing={1}>
                        <Grid item>
                            <Typography align="center" variant="subtitle1">
                                Drag and Drop or
                            </Typography>
                        </Grid>
                        <Grid item style={{ marginTop: "0.2em" }}>
                            <input
                                className={styles.FileUploadField__input}
                                id="file-upload-field-input"
                                data-testid="upload-field-input"
                                type="file"
                                onClick={clearPreviousFile}
                                {...getInputProps()}
                            />
                            <label htmlFor="file-upload-field-input">
                                <Link
                                    className={styles.FileUploadField__link}
                                    data-testid="file-browser-link"
                                    color="primary"
                                >
                                    <Typography variant="button">Browse Files</Typography>
                                </Link>
                            </label>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    )
}
